/* Side promotion bar — sections/side-promotion-bar.liquid */

const SPB_MOBILE_QUERY = "(max-width: 749px)";
const SPB_STORAGE_PREFIX = "spb-promo-seen-";
const SPB_SUBSCRIBED_COOKIE = "spb_newsletter_subscribed";
const SPB_FOCUSABLE =
  'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

class SidePromotionBar extends HTMLElement {
  connectedCallback() {
    this.backdrop = this.querySelector("[data-spb-backdrop]");
    this.dock = this.querySelector("[data-spb-dock]");
    this.peek = this.querySelector("[data-spb-peek]");
    this.peekLabel = this.querySelector("[data-spb-peek-expand]");
    this.rail = this.querySelector("[data-spb-rail]");
    this.topButton = this.querySelector("[data-spb-top]");
    this.status = this.querySelector("[data-spb-status]");
    this.panels = Array.from(this.querySelectorAll("[data-spb-panel]"));

    this.mobileQuery = window.matchMedia(SPB_MOBILE_QUERY);
    this.scrollThreshold = Number(this.dataset.scrollThreshold) || 400;
    this.openPanel = null;
    this.lastFocused = null;
    this.scrollLocked = false;
    this.scrollTicking = false;

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleBreakpoint = this.handleBreakpoint.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);

    this.addEventListener("click", this.handleClick);
    this.addEventListener("submit", this.handleFormSubmit);
    this.addEventListener("focusout", this.handleFocusOut);
    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("keydown", this.handleKeydown);
    document.addEventListener("shopify:block:select", this.handleBlockSelect);
    document.addEventListener("shopify:block:deselect", this.handleBlockDeselect);
    this.mobileQuery.addEventListener("change", this.handleBreakpoint);

    if (this.topButton) {
      window.addEventListener("scroll", this.handleScroll, { passive: true });
      window.addEventListener("resize", this.handleScroll, { passive: true });
      this.updateTopButton();
    }

    this.collapseDock();
    this.scheduleAutoOpen();
    this.restoreSubmittedPanel();
  }

  disconnectedCallback() {
    window.clearTimeout(this.settleTimer);
    this.removeEventListener("click", this.handleClick);
    this.removeEventListener("submit", this.handleFormSubmit);
    this.removeEventListener("focusout", this.handleFocusOut);
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener("shopify:block:select", this.handleBlockSelect);
    document.removeEventListener("shopify:block:deselect", this.handleBlockDeselect);
    this.mobileQuery.removeEventListener("change", this.handleBreakpoint);
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.handleScroll);
    clearTimeout(this.autoOpenTimer);
    clearTimeout(this.copyTimer);
  }

  /* ---------------- Mobile peek ---------------- */

  isMobile() {
    return this.mobileQuery.matches;
  }

  expandDock() {
    if (!this.dock) return;

    this.dock.classList.add("is-expanded");
    this.peek?.setAttribute("aria-expanded", "true");
    this.rail?.removeAttribute("inert");
    this.topButton?.removeAttribute("inert");

    if (this.peekLabel) this.peekLabel.textContent = this.peekLabel.dataset.spbPeekExpand;
  }

  collapseDock() {
    if (!this.dock) return;

    this.dock.classList.remove("is-expanded");
    this.peek?.setAttribute("aria-expanded", "false");


    if (this.isMobile()) {
      this.rail?.setAttribute("inert", "");
      this.topButton?.setAttribute("inert", "");
    } else {
      this.rail?.removeAttribute("inert");
      this.topButton?.removeAttribute("inert");
    }
  }

  handleBreakpoint() {
    this.collapseDock();
    if (this.openPanel) this.close({ restoreFocus: false });
  }

  /* ---------------- Panels ---------------- */

  isPanelModal(panel) {
    return panel.dataset.spbPanelMode === "modal" || this.isMobile();
  }

  getPanel(name) {
    return this.panels.find((panel) => panel.dataset.spbPanel === name) || null;
  }

  getTrigger(name) {
    return this.querySelector(`[data-spb-trigger="${name}"]`);
  }

  toggle(name) {
    if (this.openPanel && this.openPanel.dataset.spbPanel === name) {
      this.close();
      return;
    }
    this.open(name);
  }

  open(name) {
    const panel = this.getPanel(name);
    if (!panel) return;

    if (this.openPanel) this.close({ restoreFocus: false });

    this.lastFocused = document.activeElement;
    this.openPanel = panel;

    panel.removeAttribute("inert");
    panel.setAttribute("aria-hidden", "false");
    panel.setAttribute("aria-modal", this.isPanelModal(panel) ? "true" : "false");

    /* portal reveal: the popup grows out of its trigger icon */
    if (panel.hasAttribute("data-spb-reveal")) {
      this.setRevealOrigin(panel, name);
      window.clearTimeout(this.settleTimer);
      this.settleTimer = window.setTimeout(() => {
        if (this.openPanel === panel) panel.classList.add("is-settled");
      }, 950);
    }

    panel.classList.add("is-open");

    this.getTrigger(name)?.setAttribute("aria-expanded", "true");

    if (this.isPanelModal(panel)) {
      this.backdrop?.classList.add("is-visible");
      if (typeof AddScrollLock === "function") {
        AddScrollLock();
        this.scrollLocked = true;
      }
    }


    requestAnimationFrame(() => {
      const target = panel.querySelector(SPB_FOCUSABLE);
      target?.focus({ preventScroll: true });
    });
  }

  /* Where the trigger sits inside the popup's final box, and the circle size that covers it all */
  setRevealOrigin(panel, name) {
    const host = this.getBoundingClientRect();
    const width = panel.offsetWidth;
    const height = panel.offsetHeight;
    const left = host.left + (host.width - width) / 2;
    const top = host.top + (host.height - height) / 2;

    let x = width / 2;
    let y = height / 2;
    const rect = this.getTrigger(name)?.getBoundingClientRect();
    if (rect && rect.width && rect.height) {
      x = rect.left + rect.width / 2 - left;
      y = rect.top + rect.height / 2 - top;
    }

    const radius = Math.max(
      Math.hypot(x, y),
      Math.hypot(width - x, y),
      Math.hypot(x, height - y),
      Math.hypot(width - x, height - y),
    ) + 4;

    panel.style.setProperty("--spb-ox", `${Math.round(x)}px`);
    panel.style.setProperty("--spb-oy", `${Math.round(y)}px`);
    panel.style.setProperty("--spb-r", `${Math.ceil(radius)}px`);
  }

  close({ restoreFocus = true } = {}) {
    const panel = this.openPanel;
    if (!panel) return;

    /* close back into the trigger, which may have moved since opening */
    if (panel.hasAttribute("data-spb-reveal")) {
      window.clearTimeout(this.settleTimer);
      this.setRevealOrigin(panel, panel.dataset.spbPanel);
      panel.classList.remove("is-settled");
      void panel.offsetWidth; /* apply the full circle first so the shrink can animate */
    }

    panel.classList.remove("is-open");
    panel.setAttribute("inert", "");
    panel.setAttribute("aria-hidden", "true");

    this.getTrigger(panel.dataset.spbPanel)?.setAttribute("aria-expanded", "false");

    this.backdrop?.classList.remove("is-visible");


    if (this.scrollLocked && typeof RemoveScrollLock === "function") {
      RemoveScrollLock();
      this.scrollLocked = false;
    }

    this.openPanel = null;

    if (restoreFocus && this.lastFocused?.isConnected) {
      this.lastFocused.focus({ preventScroll: true });
    }
    this.lastFocused = null;
  }

  /* ---------------- Events ---------------- */

  handleClick(event) {
    if (event.target.closest("[data-spb-peek]")) {
      this.expandDock();
      return;
    }

    const trigger = event.target.closest("[data-spb-trigger]");
    if (trigger) {
      this.toggle(trigger.dataset.spbTrigger);
      return;
    }

    if (event.target.closest("[data-spb-close]")) {
      this.close();
      return;
    }

    const copyButton = event.target.closest("[data-spb-copy]");
    if (copyButton) {
      this.copyCode(copyButton);
      return;
    }

    if (event.target.closest("[data-spb-top]")) {
      this.scrollToTop();
    }
  }

  handleDocumentClick(event) {
    const insideBar = event.target.closest("[data-spb-panel], [data-spb-trigger], [data-spb-peek]");
    if (insideBar) return;

    if (this.openPanel) this.close();
    if (this.isMobile()) this.collapseDock();
  }

  handleKeydown(event) {
    if (event.key === "Escape" && !this.openPanel && this.dock?.classList.contains("is-expanded")) {
      this.collapseDock();
      this.peek?.focus();
      return;
    }

    if (!this.openPanel) return;

    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === "Tab" && this.isPanelModal(this.openPanel)) {
      this.trapFocus(event);
    }
  }

  handleFocusOut(event) {
    if (!this.openPanel || this.isPanelModal(this.openPanel)) return;

    const next = event.relatedTarget;
    if (!next) return;
    if (this.contains(next)) return;

    this.close({ restoreFocus: false });
  }

  trapFocus(event) {
    const focusable = Array.from(this.openPanel.querySelectorAll(SPB_FOCUSABLE)).filter(
      (el) => el.offsetParent !== null,
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------------- Theme editor ---------------- */

  handleBlockSelect(event) {
    if (!this.contains(event.target)) return;
    this.expandDock();
    this.open("offer");
  }

  handleBlockDeselect(event) {
    if (!this.contains(event.target)) return;
    this.close({ restoreFocus: false });
  }

  /* ---------------- Copy discount code ---------------- */

  async copyCode(button) {
    const code = button.dataset.spbCopy;
    if (!code) return;

    const copied = await this.writeToClipboard(code);

    button.classList.toggle("is-copied", copied);
    this.announce(copied ? button.dataset.spbCopied : button.dataset.spbCopyFailed);

    clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => button.classList.remove("is-copied"), 2500);
  }

  async writeToClipboard(text) {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {
        console.error("Side promotion bar: clipboard write failed", error);
      }
    }

    const field = document.createElement("textarea");
    field.value = text;
    field.setAttribute("readonly", "");
    field.style.position = "fixed";
    field.style.opacity = "0";
    document.body.appendChild(field);
    field.select();

    let copied = false;
    try {
      copied = document.execCommand("copy");
    } catch (error) {
      console.error("Side promotion bar: clipboard fallback failed", error);
    }

    field.remove();
    return copied;
  }

  announce(message) {
    if (!this.status || !message) return;
    this.status.textContent = "";
    requestAnimationFrame(() => {
      this.status.textContent = message;
    });
  }

  /* ---------------- Back to top ---------------- */

  handleScroll() {
    if (this.scrollTicking) return;
    this.scrollTicking = true;

    requestAnimationFrame(() => {
      this.updateTopButton();
      this.scrollTicking = false;
    });
  }

  updateTopButton() {
    if (!this.topButton) return;

    const scrolled = window.scrollY || document.documentElement.scrollTop || 0;
    const show = scrolled > this.scrollThreshold;

    this.topButton.classList.toggle("is-visible", show);
    this.topButton.setAttribute("aria-hidden", show ? "false" : "true");

    if (show) {
      this.topButton.removeAttribute("tabindex");
    } else {
      this.topButton.setAttribute("tabindex", "-1");
    }
  }

  scrollToTop() {
    if (typeof lenis !== "undefined" && lenis?.scrollTo) {
      lenis.scrollTo(0);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  /* ---------------- Automatic newsletter popup ---------------- */

  /* Opens once per visit (session), and not at all for a visitor who has subscribed */
  scheduleAutoOpen() {
    const name = this.dataset.autoOpen;
    if (!name || !this.getPanel(name)) return;
    if (window.Shopify?.designMode) return;
    if (this.isSubscribed() || this.hasSeenAutoOpen()) return;

    const delay = (Number(this.dataset.autoOpenDelay) || 5) * 1000;

    this.autoOpenTimer = setTimeout(() => {
      if (this.openPanel) return;
      this.open(name);
      this.rememberAutoOpen();
    }, delay);
  }

  storageKey() {
    return SPB_STORAGE_PREFIX + (this.dataset.autoOpenKey || "default");
  }

  hasSeenAutoOpen() {
    try {
      return sessionStorage.getItem(this.storageKey()) === "true";
    } catch (error) {
      return false;
    }
  }

  rememberAutoOpen() {
    try {
      sessionStorage.setItem(this.storageKey(), "true");
    } catch (error) {
      /* Nothing to do when storage is unavailable */
    }
  }

  /* ---------------- Subscribed cookie (30 / 60 / 90 days) ---------------- */

  isSubscribed() {
    return document.cookie.split("; ").some((part) => part.startsWith(`${SPB_SUBSCRIBED_COOKIE}=`));
  }

  rememberSubscribed() {
    const days = Number(this.dataset.subscribeDays) || 30;
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${SPB_SUBSCRIBED_COOKIE}=1; max-age=${days * 86400}; path=/; SameSite=Lax${secure}`;
  }

  /* Valid email: let the form submit (the page reloads) and remember it was this popup */
  handleFormSubmit(event) {
    if (event.defaultPrevented) return; /* invalid email: newsletter-form stopped it */
    if (!event.target.closest('[data-spb-panel="promo"]')) return;

    try {
      sessionStorage.setItem(`${this.storageKey()}-submitted`, "true");
    } catch (error) {
      /* the panel simply will not reopen after the reload */
    }
  }

  /* After the reload: reopen to show the result, and set the cookie on success */
  restoreSubmittedPanel() {
    let submitted = false;
    try {
      submitted = sessionStorage.getItem(`${this.storageKey()}-submitted`) === "true";
      sessionStorage.removeItem(`${this.storageKey()}-submitted`);
    } catch (error) {
      submitted = false;
    }

    const panel = this.getPanel("promo");
    if (!submitted || !panel) return;
    if (!new URLSearchParams(window.location.search).has("customer_posted")) return;

    if (panel.querySelector(".spb_form_success")) this.rememberSubscribed();

    clearTimeout(this.autoOpenTimer);
    requestAnimationFrame(() => this.open("promo"));
  }
}

if (!customElements.get("side-promotion-bar")) {
  customElements.define("side-promotion-bar", SidePromotionBar);
}
